import React, { useState, useRef, useEffect } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { useCategories } from '../../../../hooks/useCategories';
import styles from './CategorySelector.module.scss';

let debounceTimer;

const CategorySelector = ({ 
  formData, 
  errors, 
  onCategorySelect,
  categorySearchTerm,
  setCategorySearchTerm,
  selectedCategoryName,
  setSelectedCategoryName,
  clearError,
  showPopup // Add this prop to show popup messages
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const dropdownRef = useRef(null);

  const { filteredCategories, loading, fetchCategories } = useCategories();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🔁 Debounced search function with error handling
  const debouncedSearch = async (value) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      if (value.trim()) {
        try {
          await fetchCategories(value);
          setShowDropdown(true);
        } catch (error) {
          console.error('Category search error:', error);
          // Show error in popup instead of local state
          if (showPopup) {
            showPopup('error', 'Category Search Failed', error.message);
          }
          setShowDropdown(false);
        }
      } else {
        setShowDropdown(false);
      }
    }, 300);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setCategorySearchTerm(value);
    setIsUserTyping(true);

    if (value !== selectedCategoryName) {
      setSelectedCategoryName('');
    }

    clearError('category_id');
    debouncedSearch(value);
  };

  const handleCategoryClick = (category) => {
    onCategorySelect(category);
    setCategorySearchTerm(category.categoryName);
    setSelectedCategoryName(category.categoryName);
    setShowDropdown(false);
    setIsUserTyping(false);
    clearError('category_id');
  };

  const handleInputFocus = () => {
    clearError('category_id');
    if (!selectedCategoryName) {
      setIsUserTyping(true);
    }
  };

  return (
    <div className={`${styles.label_input} ${errors.category_id ? styles.error : ''}`}>
      <label>Category Name<span>*</span></label>
      <div className={styles.input_container}>
        <div className={`${styles.search_container}`} ref={dropdownRef}>
          <div className={`${styles.search_input_wrapper}`}>
            <input
              type="text"
              name="category_search"
              value={categorySearchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              autoComplete='off'
              onBlur={() => {
                setTimeout(() => setIsUserTyping(false), 100);
              }}
              placeholder="Search for Category"
            />
            <button
              type="button"
              className={`${styles.search_button}`}
              onClick={() => debouncedSearch(categorySearchTerm)}
            >
              <AiOutlineSearch />
            </button>
          </div>

          {showDropdown && (
            <div className={`${styles.category_dropdown}`}>
              {loading ? (
                <div className={styles.loading_option}>
                  Searching categories...
                </div>
              ) : filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <div
                    key={category.categoryId}
                    className={`${styles.category_option}`}
                    onMouseDown={() => handleCategoryClick(category)}
                  >
                    <div className={`${styles.category_name}`}>{category.categoryName}</div>
                    <div className={`${styles.category_path}`}>
                      {category.categoryPath}
                    </div>
                  </div>
                ))
              ) : (
                <div
                  className={`${styles.category_option} ${styles.category_name}`}
                  onMouseDown={() => handleCategoryClick({ categoryId: 'no-category', categoryName: 'No Category' })}
                >
                  No Category
                </div>
              )}
            </div>
          )}
        </div>
        {errors.category_id && <span className={styles.errorMessage}>{errors.category_id}</span>}
      </div>
    </div>
  );
};

export default CategorySelector;